<script setup>
import {
	Dropdown,
	DmIconButton,
	DmTag,
	List,
	ListBody,
	ListCell,
	ListColumn,
	ListHeader,
	ListRow,
	ListTypingFilter,
	RiskEntity,
	OpportunityEntity,
	PositiveImpactEntity,
	NegativeImpactEntity,
	url
} from 'dmui_components'

const DEBOUNCE_TIMER = 500
const props = defineProps({
	iroData: {
		type: Array,
		default: undefined
	},
	search: {
		type: Object,
		default: () => ({})
	},
	readonly: {
		type: Boolean,
		default: false
	}
})

const MIN_SEARCH_LIMIT = 2

const emit = defineEmits(['update:search', 'update:status'])

function openReport(url) {
	window.open(url, '_blank')
}

function onUpdateSearch(key, value) {
	if (value?.length === 0 || value?.length > MIN_SEARCH_LIMIT) {
		emit('update:search', { ...props.search, [key]: value })
	}
}

function updateStatus(reportIro, status) {
	reportIro.status = status
	emit('update:status', reportIro)
}

const iroStatuses = [
	{ value: 'In progress', tagIcon: 'in-progress', tagType: 'warning' },
	{ value: 'Done', tagIcon: 'check-circle-solid', tagType: 'primary' },
	{ value: 'To do', tagIcon: 'circle-dashed', tagType: 'neutral' }
]
</script>
<template>
	<List>
		<ListHeader>
			<ListTypingFilter :timer="DEBOUNCE_TIMER" :data="search.org_name" @update:data="onUpdateSearch('org_name', $event)"> Company </ListTypingFilter>
			<ListColumn>Pages</ListColumn>
			<ListColumn>CSRD IROs</ListColumn>
			<ListColumn>Entity-specific IROs</ListColumn>
			<ListColumn id="status">Status</ListColumn>
		</ListHeader>
		<ListBody>
			<slot>
				<ListRow v-for="reportIro in iroData" :key="reportIro.report_id">
					<ListCell>
						<span class="multiline-data">
							<div class="data-container">
								<router-link :to="url`report/iros/${reportIro.report_id}`" :title="reportIro?.org_name" class="primary-link">{{ reportIro.org_name }}</router-link>
								<DmIconButton
									:id="`${reportIro.report_id}-download-button`"
									class="primary transparent slim"
									icon="uil-download-alt"
									icon-size="midi"
									@click="openReport(reportIro?.source_url)">
									Download
								</DmIconButton>
								<DmIconButton
									:id="`${reportIro.report_id}-open-file-button`"
									class="primary transparent slim"
									icon="uil-external-link-alt"
									icon-size="midi"
									@click="openReport(`https://app.datamaran.com/#/report_viewer/${reportIro?.document_title}`)">
									Open
								</DmIconButton>
							</div>
							<div class="data-container">
								<div class="secondary-text bold doc-type">{{ reportIro.document_type }} CSRD</div>
								<div>{{ reportIro.document_year }}</div>
							</div>
						</span>
					</ListCell>
					<ListCell id="pages">
						<div v-for="(page, index) in reportIro.page_ranges" :key="index">{{ page.start_page }} - {{ page.end_page }}</div>
					</ListCell>
					<ListCell>
						<div class="iro-stats">
							<div class="stat">
								<component :is="RiskEntity" size="medium" />
								<div class="iro-count">{{ reportIro.all_category_counts.risks }}</div>
							</div>
							<div class="stat">
								<component :is="OpportunityEntity" size="medium" />
								<div class="iro-count">{{ reportIro.all_category_counts.opportunities }}</div>
							</div>
							<div class="stat">
								<component :is="PositiveImpactEntity" size="medium" />
								<div class="iro-count">{{ reportIro.all_category_counts.positive_impacts }}</div>
							</div>
							<div class="stat">
								<component :is="NegativeImpactEntity" size="medium" />
								<div class="iro-count">{{ reportIro.all_category_counts.negative_impacts }}</div>
							</div>
						</div>
					</ListCell>
					<ListCell>
						<div class="iro-stats">
							<div class="stat">
								<component :is="RiskEntity" size="medium" />
								<div class="iro-count">{{ reportIro.entity_specific_category_counts.risks }}</div>
							</div>
							<div class="stat">
								<component :is="OpportunityEntity" size="medium" />
								<div class="iro-count">{{ reportIro.entity_specific_category_counts.opportunities }}</div>
							</div>
							<div class="stat">
								<component :is="PositiveImpactEntity" size="medium" />
								<div class="iro-count">{{ reportIro.entity_specific_category_counts.positive_impacts }}</div>
							</div>
							<div class="stat">
								<component :is="NegativeImpactEntity" size="medium" />
								<div class="iro-count">{{ reportIro.entity_specific_category_counts.negative_impacts }}</div>
							</div>
						</div>
					</ListCell>
					<ListCell>
						<Dropdown
							:id="`${reportIro.report_id}-status-dropdown`"
							:data="iroStatuses?.find((status) => status.value === reportIro.status)"
							:read-only="readonly"
							appearance="transparent"
							size="small"
							:options="iroStatuses"
							@update:data="updateStatus(reportIro, $event?.value)">
							<template #selected="{ data }">
								<DmTag :class="data.tagType">{{ data.value }}</DmTag>
							</template>
							<template #default="{ data }">
								<DmTag :class="data.tagType">{{ data.value }}</DmTag>
							</template>
						</Dropdown>
					</ListCell>
				</ListRow>
			</slot>
		</ListBody>
	</List>
</template>

<style scoped lang="less">
.data-container {
	display: flex;
	gap: @micro;
	text-transform: capitalize;
	align-items: center;
}
.multiline-data {
	display: flex;
	flex-direction: column;
	.tiny-text {
		color: @gray-80;
		line-height: 14px;
	}
}

:deep(tbody tr):hover {
	background-color: @gray-10;
}

:deep(td),
:deep(th) {
	&:not(:last-child) {
		max-width: 100px;
		min-width: 100px;
	}
	&:nth-child(1),
	&:nth-child(2) {
		min-width: 190px;
		max-width: 190px;
	}
	&:nth-child(3),
	&:nth-child(4) {
		min-width: 280px;
		max-width: 280px;
	}
	&:nth-child(5) {
		min-width: 120px;
		max-width: 120px;
	}
}

:deep(td):first-child {
	padding-left: @micro !important;
}
:deep(td):last-child {
	padding-right: @micro !important;
}

.report-url {
	display: flex;
	align-items: center;
	gap: @micro;
	span {
		color: @dm-link;
	}
}

#pages {
	display: flex;
	gap: @micro;
	flex-wrap: wrap;
	& > *:not(:last-child) {
		&::after {
			content: ', ';
		}
	}
}

.iro-stats {
	display: grid;
	grid-template-columns: repeat(4, 65px);
	.stat {
		display: flex;
		gap: @micro;
	}
}

#status {
	padding-left: @midi;
}
</style>
